import { Textbox } from 'fabric';
import type { SobreimpresoMeta, SobreimpresoTextbox } from './types';
import { fillToCanvasStyle } from '../../shared/utils/fill';
import { drawRoundedRect } from '../../shared/utils/draw';

type TextboxProto = Textbox & { sobreimpreso?: SobreimpresoMeta };

const originalRenderBackground = Textbox.prototype._renderBackground;

Textbox.prototype._renderBackground = function (ctx: CanvasRenderingContext2D) {
  const meta = (this as TextboxProto).sobreimpreso;

  if (!meta) {
    originalRenderBackground.call(this, ctx);
    return;
  }

  const w = this.width ?? 0;
  const h = this.height ?? 0;

  ctx.save();
  drawRoundedRect(ctx, -w / 2, -h / 2, w, h, meta.borderRadius);
  ctx.fillStyle = fillToCanvasStyle(ctx, meta.fill, -w / 2, -h / 2, w, h);
  ctx.fill();

  if (meta.strokeWidth > 0 && meta.stroke) {
    ctx.strokeStyle = meta.stroke;
    ctx.lineWidth = meta.strokeWidth;
    ctx.stroke();
  }
  ctx.restore();
};

const originalWrapText = Textbox.prototype._wrapText;

Textbox.prototype._wrapText = function (lines: string[], desiredWidth: number): string[][] {
  const meta = (this as TextboxProto).sobreimpreso;
  const adjusted = meta ? Math.max(1, desiredWidth - 2 * meta.paddingX) : desiredWidth;
  return originalWrapText.call(this, lines, adjusted);
};

// Padding that satisfies both paddingY and the minHeight floor.
function effectivePaddingY(meta: SobreimpresoMeta, baseTextHeight: number): number {
  const minPad = (meta.minHeight - baseTextHeight) / 2;
  return Math.max(meta.paddingY, minPad);
}

const originalCalcTextHeight = Textbox.prototype.calcTextHeight;

Textbox.prototype.calcTextHeight = function (): number {
  const base = originalCalcTextHeight.call(this);
  const meta = (this as TextboxProto).sobreimpreso;
  return meta ? base + 2 * effectivePaddingY(meta, base) : base;
};

/** Natural banner height with current text + paddingY, ignoring the minHeight floor. */
export function getSobreimpresoNaturalHeight(s: SobreimpresoTextbox): number {
  const base = originalCalcTextHeight.call(s);
  return base + 2 * s.sobreimpreso.paddingY;
}

const originalGetTopOffset = Textbox.prototype._getTopOffset;

Textbox.prototype._getTopOffset = function (): number {
  const base = originalGetTopOffset.call(this);
  const meta = (this as TextboxProto).sobreimpreso;
  if (!meta) return base;
  const baseTextHeight = originalCalcTextHeight.call(this);
  return base + effectivePaddingY(meta, baseTextHeight);
};

const originalGetLineLeftOffset = Textbox.prototype._getLineLeftOffset;

Textbox.prototype._getLineLeftOffset = function (lineIndex: number): number {
  const base = originalGetLineLeftOffset.call(this, lineIndex);
  const meta = (this as TextboxProto).sobreimpreso;
  if (!meta) return base;

  if (this.textAlign === 'left') return base + meta.paddingX;
  if (this.textAlign === 'right') return base - meta.paddingX;
  return base;
};

export function resizeSobreimpresoHeight(s: SobreimpresoTextbox): void {
  s.initDimensions();
  s.setCoords();
  s.dirty = true;
  s.canvas?.requestRenderAll();
}
